import { useQuery } from '@tanstack/react-query'
import { useMedplum } from '@medplum/react'
import type { MedicationRequest, Practitioner, Resource } from '@medplum/fhirtypes'
import { isDoseSpotPrescription } from '@/pages/Patient/components/doseSpotPrescription'
import { logError } from '@/utils/errorUtils'
import { formatPatientName } from '@/utils'
import { doseSpotPrescriptionsQueryKey } from './doseSpotPrescriptionsQueryKey'

// Volumes are tens of rows per patient (§11.3), so one page is the whole list.
const PAGE_SIZE = 100

/**
 * The DoseSpot half of the chart's Medications section: the prescriptions the write-back
 * sync landed as `MedicationRequest`s, newest first, with the mapped prescriber's name
 * already resolved.
 *
 * Kept as its own query rather than folded into `ResourceTable`'s search so that **a
 * DoseSpot failure cannot empty the note-authored medication list** — a failed read logs
 * and yields no rows, and the `MedicationStatement` table beside it is untouched.
 *
 * The query key is what `useDoseSpotPrescriptionSync` invalidates, so a sync that lands
 * after the chart painted refreshes these rows in place.
 *
 * Deliberately **not** gated on the patient carrying a DoseSpot identifier, unlike the
 * chart-view sync: the guarantee is that the chart is correct whenever anyone looks at it,
 * and one cheap search that usually returns nothing is a better trade than a chart that
 * hides real prescriptions whenever the identifier goes missing (a merge, a manual edit).
 */
export function useDoseSpotPrescriptions(patientId: string | undefined): MedicationRequest[] {
  const medplum = useMedplum()

  const { data } = useQuery({
    queryKey: doseSpotPrescriptionsQueryKey(patientId ?? 'none'),
    enabled: !!patientId,
    queryFn: async (): Promise<MedicationRequest[]> => {
      try {
        const bundle = await medplum.search(
          'MedicationRequest',
          // `_include` resolves the requester in the same round trip; the sync writes
          // `requester` as a bare reference and leaves naming to whoever renders it.
          `subject=Patient/${patientId}&_include=MedicationRequest:requester&_count=${PAGE_SIZE}&_sort=-authoredon`,
        )
        // Typed as the searched resource by `medplum.search`, but `_include` puts the
        // requester `Practitioner` in the same bundle — hence the widened element type.
        const resources: Resource[] =
          bundle.entry?.flatMap((entry) => (entry.resource ? [entry.resource] : [])) ?? []
        const practitioners = resources.filter(
          (resource): resource is Practitioner => resource.resourceType === 'Practitioner',
        )

        return resources
          .filter(isDoseSpotPrescription)
          .map((prescription) => withRequesterName(prescription, practitioners))
      } catch (error) {
        logError('dosespot', 'failed to read DoseSpot prescriptions for the chart', error)
        return []
      }
    },
  })

  return data ?? []
}

/**
 * Fills `requester.display` from the included `Practitioner`, leaving an **absent**
 * requester absent — that absence is exactly the unmapped-prescriber flag the row label
 * reads (§11.6), so nothing here may invent one.
 */
function withRequesterName(
  prescription: MedicationRequest,
  practitioners: Practitioner[],
): MedicationRequest {
  const reference = prescription.requester?.reference
  if (!reference || prescription.requester?.display) return prescription

  const practitioner = practitioners.find((p) => `Practitioner/${p.id}` === reference)
  if (!practitioner) return prescription

  const display = formatPatientName(practitioner.name?.[0])
  if (!display) return prescription

  return { ...prescription, requester: { ...prescription.requester, display } }
}
